import { Menu, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, Outlet } from 'react-router';
import { LanguageSwitcher, Logo } from '../components/brand';
import { Button } from '../components/ui';
import { useAuth } from '../lib/auth';

const NAV = [
  { to: '/features', key: 'public.nav.features' },
  { to: '/industries', key: 'public.nav.industries' },
  { to: '/integrations', key: 'public.nav.integrations' },
  { to: '/ai', key: 'public.nav.ai' },
  { to: '/pricing', key: 'public.nav.pricing' },
  { to: '/help', key: 'public.nav.help' },
];

export function PublicLayout() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <header className="sticky top-0 z-40 border-b border-slate-200/70 bg-white/90 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6">
          <Logo />
          <nav className="hidden items-center gap-1 lg:flex">
            {NAV.map((item) => (
              <NavLink key={item.to} to={item.to}
                className={({ isActive }) => `rounded-lg px-3 py-2 text-sm font-medium ${isActive ? 'text-brand-700' : 'text-slate-600 hover:text-slate-900'}`}>
                {t(item.key)}
              </NavLink>
            ))}
          </nav>
          <div className="hidden items-center gap-2 lg:flex">
            <LanguageSwitcher compact />
            {user ? (
              <Link to="/app"><Button>{t('public.nav.openApp')}</Button></Link>
            ) : (
              <>
                <Link to="/login"><Button variant="ghost">{t('public.nav.login')}</Button></Link>
                <Link to="/register"><Button>{t('public.nav.startFree')}</Button></Link>
              </>
            )}
          </div>
          <button type="button" className="rounded-lg p-2 text-slate-600 lg:hidden" onClick={() => setOpen((o) => !o)} aria-label={t('common.menu')}>
            {open ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>
        {open && (
          <div className="border-t border-slate-200 bg-white px-4 py-3 lg:hidden">
            <nav className="flex flex-col">
              {NAV.map((item) => (
                <Link key={item.to} to={item.to} onClick={() => setOpen(false)} className="rounded-lg px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
                  {t(item.key)}
                </Link>
              ))}
            </nav>
            <div className="mt-3 flex items-center gap-2 border-t border-slate-100 pt-3">
              <LanguageSwitcher compact />
              <Link to={user ? '/app' : '/login'} className="flex-1"><Button variant="secondary" className="w-full">{user ? t('public.nav.openApp') : t('public.nav.login')}</Button></Link>
              {!user && <Link to="/register" className="flex-1"><Button className="w-full">{t('public.nav.startFree')}</Button></Link>}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1"><Outlet /></main>

      <footer className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto grid max-w-7xl gap-8 px-4 py-12 sm:grid-cols-2 sm:px-6 lg:grid-cols-4">
          <div>
            <Logo />
            <p className="mt-3 max-w-xs text-sm text-slate-500">{t('public.footer.tagline')}</p>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-slate-900">{t('public.footer.product')}</h4>
            <ul className="mt-3 space-y-2 text-sm text-slate-600">
              <li><Link to="/features">{t('public.nav.features')}</Link></li>
              <li><Link to="/pricing">{t('public.nav.pricing')}</Link></li>
              <li><Link to="/ai">{t('public.nav.ai')}</Link></li>
              <li><Link to="/integrations">{t('public.nav.integrations')}</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-slate-900">{t('public.footer.company')}</h4>
            <ul className="mt-3 space-y-2 text-sm text-slate-600">
              <li><Link to="/industries">{t('public.nav.industries')}</Link></li>
              <li><Link to="/help">{t('public.nav.help')}</Link></li>
              <li><Link to="/contact">{t('public.footer.contact')}</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-slate-900">{t('public.footer.legal')}</h4>
            <ul className="mt-3 space-y-2 text-sm text-slate-600">
              <li><Link to="/terms">{t('public.footer.terms')}</Link></li>
              <li><Link to="/privacy">{t('public.footer.privacy')}</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-200 py-5 text-center text-xs text-slate-500">© {new Date().getFullYear()} High CRM · {t('public.footer.madeIn')}</div>
      </footer>
    </div>
  );
}
